import axios from "axios";

const BASE_URL = process.env.REACT_APP_AUTH_SERVICE_BASE_URL;

export interface Partner {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  offerTypeId: string;
  agencyCode: string;
  countryCode: string;
  paymentFrequency?: number;
  lastPayoutDate?: string;
  registrationNumber?: string;
  companyName?: string;
  driversCount?: number;
  carsCount?: number;
  totalEarnings?: number;
  pendingAmount?: number;
  paidAmount?: number;
  status?: string;
}

export interface CreatePartnerDto {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  phone: string;
  offerTypeId: string;
  countryCode: string;
  paymentFrequency?: number;
  registrationNumber?: string;
  companyName?: string;
}

export interface UpdatePartnerDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  offerTypeId?: string;
  paymentFrequency?: number;
  registrationNumber?: string;
  companyName?: string;
  password?: string;
}

export interface PartnerResponse {
  status?: string | boolean;
  data?: {
    partners: Partner[];
    total: number;
    page: number;
    limit: number;
  };
  // Direct response format (when not wrapped)
  partners?: Partner[];
  total?: number;
  page?: number;
  limit?: number;
}

export const partnerService = {
  getPartners: async (page: number = 1, limit: number = 10): Promise<PartnerResponse> => {
    const token = localStorage.getItem("token");
    const { data } = await axios.get<PartnerResponse>(
      `${BASE_URL}/auth/v1/users/partners`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit },
      }
    );
    return data;
  },

  getPartnerById: async (partnerId: string): Promise<Partner> => {
    const token = localStorage.getItem("token");
    const { data } = await axios.get<{ status?: string | boolean; data?: Partner } | Partner>(
      `${BASE_URL}/auth/v1/users/partners/${partnerId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    // Handle both wrapped response (status/data) and direct response
    // Check if it's a wrapped response: has 'status' (boolean or "success"/"error") AND 'data' property
    if (data && typeof data === 'object' && 'status' in data && 'data' in data) {
      // Wrapped response format: { status: true/false/"success", data: Partner }
      const wrappedData = data as { status?: string | boolean; data?: Partner };
      if (wrappedData.data && typeof wrappedData.data === 'object' && '_id' in wrappedData.data) {
        return wrappedData.data;
      }
    }
    // Direct response format: Partner object with _id
    if (data && typeof data === 'object' && '_id' in data) {
      return data as Partner;
    }
    throw new Error('Invalid response format');
  },

  createPartner: async (partnerData: CreatePartnerDto): Promise<Partner> => {
    const token = localStorage.getItem("token");
    const { data } = await axios.post<{ status: string; data: Partner }>(
      `${BASE_URL}/auth/v1/users/partners`,
      partnerData,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return data.data;
  },

  updatePartner: async (partnerId: string, partnerData: UpdatePartnerDto): Promise<Partner> => {
    const token = localStorage.getItem("token");
    const { data } = await axios.put<{ status: string; data: Partner }>(
      `${BASE_URL}/auth/v1/users/partners/${partnerId}`,
      partnerData,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return data.data;
  },

  deletePartner: async (partnerId: string): Promise<void> => {
    const token = localStorage.getItem("token");
    await axios.post(
      `${BASE_URL}/auth/v1/users/partners/${partnerId}/delete`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
  },

  getAllPartnersEarningsSummary: async (): Promise<Record<string, { total_earnings: number; paid_amount: number; pending_amount: number; total_rides: number }>> => {
    const token = localStorage.getItem("token");
    const ORDERS_BASE_URL = process.env.REACT_APP_ORDERS_SERVICE_BASE_URL;
    const { data } = await axios.get<{ status: string; data: { partners_earnings: Record<string, any> } }>(
      `${ORDERS_BASE_URL}/api/v1/partner/earnings-summary/all`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return data.data.partners_earnings;
  },

  triggerPartnerPayouts: async (): Promise<{ message: string; processed: number; total: number }> => {
    const token = localStorage.getItem("token");
    const ORDERS_BASE_URL = process.env.REACT_APP_ORDERS_SERVICE_BASE_URL;
    if (!ORDERS_BASE_URL) {
      console.error("ORDERS_SERVICE_BASE_URL is not defined");
      throw new Error("Orders service URL is not configured");
    }
    const { data } = await axios.post<{ status: string; data: { message: string; processed: number; total: number } }>(
      `${ORDERS_BASE_URL}/api/v1/partner/process-payouts`,
      {},
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return data.data;
  },
};

